import * as React from "react";
import * as yup from "yup";
import { Form, Modal, Button, Checkbox, ModalActions, ModalContent, FormTextArea, FormInput, FormGroup, ModalHeader } from "@/components/bp/bp-ui";
import { AgencyReadModel, AgencyCreateModel, AgencyDetailsModel } from "@app/api2/api";
import { ErrorMessage, Formik } from "formik";
import SemanticField from "../../SemanticField";
import 'react-confirm-alert/src/react-confirm-alert.css'; // Import css
import { areYouSure2 } from "../AreYouSure";
import { BPReactTable2, convertColumns, useBpReactTable2 } from "../Common/DataTable/BPReactTable2";
import { useTranslation2 } from "../Contexts/TranslationContext2";
import { labClient } from "@app/api2/Clients";
import { BPButton } from "@/components/bp/bpbutton";

export interface LabAgenciesViewProps {
    readOnly: boolean;
    labId: number;
}

export function LabAgenciesViewFC(props: LabAgenciesViewProps) {
    const { translations: t } = useTranslation2()
    const [showDisabled, setShowDisabled] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editedAgency, setEditedAgency] = React.useState<AgencyDetailsModel>({ name: '', address: '', id: 0, isActive: true });
    const validationSchema = yup.object().shape({
        name: yup.string().required().label(window.translation.case.name),
    });

    const columnDefs = convertColumns<AgencyReadModel>([
        {
            accessor: 'id',
            Header: 'Id',
            Cell: (cell) => <>{cell.value}</>,
            width: 70
        },
        {
            accessor: 'name',
            Header: t.common.name,
            Cell: (cell) => <a href="#" onClick={(e) => {
                e.preventDefault();
                editAgency(cell.row.original.id);
            }}>{cell.value}</a>
        }
    ]);

    const table = useBpReactTable2<AgencyReadModel>({
        queryKeyPrefix: 'LabAgencies' + showDisabled,
        columnDefs: columnDefs,
        query: async (paging, sorting, searchValue, filters) => {
            let result = await labClient.getLabAgencies(props.labId, showDisabled, sorting[0]?.id, sorting[0]?.desc ? 'desc' : 'asc');
            return { rows: result.agencies, rowCount: result.agencies.length };
        },
    });


    async function addAgency(agency: AgencyCreateModel) {
        let res = await labClient.createLabAgency(props.labId, agency);
        await table.dataQuery.refetch({})
    }

    async function updateAgency(agency: AgencyDetailsModel) {
        let res = await labClient.updateLabAgency(props.labId, agency.id, { name: agency.name, address: agency.address });
        await table.dataQuery.refetch({})
    }

    // @areYouSure(() => { return { message: window.translation.lab.deleteAgencyConfirm, title: window.translation.common.confirmDisable }; })
    async function toggleAgencyEnabled(id: number) {
        let res = editedAgency.isActive ? await labClient.inactivateLabAgency(props.labId, id) : await labClient.activateLabAgency(props.labId, id);
        await table.dataQuery.refetch({})
        setOpen(false);
    }

    // @areYouSure(() => { return { message: window.translation.lab.removeAgencyDataConfirm, title: window.translation.common.confirmDelete }; })
    async function deleteAgencyData(id: number) {
        areYouSure2({
            message: window.translation.lab.removeAgencyDataConfirm,
            title: window.translation.common.confirmDelete,
            action: async () => {
                let res = await labClient.removeLabAgencyData(props.labId, id);
                setOpen(false);
                await table.dataQuery.refetch({})
            }
        })
    }

    function createNewAgency() {
        setEditedAgency({ name: '', address: '', id: 0, isActive: true });
        setDialogOpen(true);
    }

    async function editAgency(id: number) {
        let res = await labClient.getLabAgency(props.labId, id);
        setEditedAgency(res.agency);
        setDialogOpen(true);
    }

    function setOpen(value: boolean) {
        setDialogOpen(value);
    }



    return <>
        <FormGroup>
            <BPButton onClick={() => createNewAgency()}>{t.common.createNew}</BPButton>
            <Checkbox
                label={window.translation.lab.showDisabledAgencies}
                checked={showDisabled}
                onChange={(e, { checked }) => { setShowDisabled(checked); }} />
        </FormGroup>
        <BPReactTable2 {...table} disablePagination deletedSelector={(row: any) => !row.isActive} disableGlobalFilter />
        <Modal open={dialogOpen} onClose={() => setOpen(false)} onOpen={() => setOpen(true)}>
            <ModalHeader>{editedAgency.id <= 0 ? t.lab.addAgency : `${t.lab.editAgency} ${editedAgency.name}`}</ModalHeader>
            <ModalContent>
                <Formik initialValues={{ id: editedAgency.id, name: editedAgency.name, address: editedAgency.address, isActive: editedAgency.isActive }}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        if (values.id == 0)
                            await addAgency(values);

                        else
                            await updateAgency(values);
                        setSubmitting(false);
                        setOpen(false);
                    }}>
                    {({ isSubmitting, handleSubmit }) => (
                        <Form id="form" onSubmit={handleSubmit}>
                            <SemanticField component={FormInput} id="name" label="Name" name="name" required />
                            <ErrorMessage name="name" component="div" />
                            <SemanticField component={FormTextArea} id="address" label="Address" type="address" name="address" />
                            <ErrorMessage name="address" component="div" />
                        </Form>
                    )}
                </Formik>
            </ModalContent>
            <ModalActions>
                <BPButton floated="left" onClick={() => deleteAgencyData(editedAgency.id)} negative>
                    {t.lab.removeAgencyData}
                </BPButton>
                <BPButton onClick={() => toggleAgencyEnabled(editedAgency.id)}>
                    {editedAgency.isActive ? t.common.disable : t.common.enable}
                </BPButton>
                <BPButton form="form" type="submit">
                    {t.common.save}
                </BPButton>
                <BPButton onClick={() => setOpen(false)}>
                    {t.common.close}
                </BPButton>
            </ModalActions>
        </Modal>
    </>;
}
